import type { ChannelSummary, ReplyBotInfo } from '../api/types'
import type { UserMap } from './channels'

// Restricted channels (contract: "Restricted channels"). Only owners, posters and installed bots post in them;
// everyone else reads, reacts and, when a reply bot is available, replies privately to it. Every helper
// works from the local channel row, so the answer stays right live as events change posting, roles or archive.

type PostingFields = Pick<ChannelSummary, 'type' | 'isMember' | 'archivedAt' | 'myRole' | 'bot'> & {
  /** Absent on rows and summaries from before restricted channels existed. */
  posting?: ChannelSummary['posting']
  replyBot?: ChannelSummary['replyBot']
}

export function isRestricted(channel: Pick<PostingFields, 'type' | 'posting'>): boolean {
  return channel.type === 'channel' && channel.posting === 'restricted'
}

/** The posting rule for my role alone (not membership or archive): owners and posters post in a restricted channel. */
export function roleCanPost(channel: Pick<PostingFields, 'type' | 'posting' | 'myRole'>): boolean {
  return !isRestricted(channel) || channel.myRole === 'owner' || channel.myRole === 'poster'
}

/** Whether I can post here now: membership, archive, a bot conversation's webhook and the posting rule. */
export function canPostIn(channel: PostingFields): boolean {
  if (!channel.isMember || channel.archivedAt) return false
  if (channel.type === 'bot_dm') return Boolean(channel.bot?.acceptsMessages)
  return roleCanPost(channel)
}

/** Pinning follows the posting rule: in a restricted channel only owners and posters pin. */
export function canPinIn(channel: PostingFields): boolean {
  return channel.isMember && !channel.archivedAt && roleCanPost(channel)
}

/** The reply bot of a restricted channel, when there is one (available or paused). */
export function replyBotOf(channel: Pick<PostingFields, 'type' | 'posting' | 'replyBot'>): ReplyBotInfo | null {
  return isRestricted(channel) ? (channel.replyBot ?? null) : null
}

/** "Reply privately" is offered to members who can't post while the channel's reply bot is available. */
export function canReplyPrivately(channel: PostingFields): boolean {
  return channel.isMember && !channel.archivedAt && !roleCanPost(channel) && Boolean(replyBotOf(channel)?.available)
}

/** The reply bot's name: its user's display name when known locally, else the name in the summary. */
export function replyBotName(replyBot: ReplyBotInfo, users: UserMap): string {
  return users.get(replyBot.userId)?.displayName || replyBot.name
}

/** What someone who can't post sees instead of the composer. */
export function restrictedNote(channel: Pick<PostingFields, 'type' | 'posting' | 'replyBot'>, users: UserMap): { title: string; detail: string | null } {
  const bot = replyBotOf(channel)
  return {
    title: 'Only some people can post here.',
    detail: bot ? (bot.available ? `Reply to a message to talk to ${replyBotName(bot, users)}.` : 'Replies are paused.') : null,
  }
}
